/**
 * «Otros» es una cola, no un cajón (ADR-031 D4, GKTIBO-147).
 *
 * Lo que no cabe en las seis preguntas cae en «Otros» y ahí se queda sin
 * nombre. Cuando tres o más cosas comparten asunto, Tibo pregunta si las
 * agrupa en una sección con nombre. El dueño decide; nadie escribe sin él.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api.ts';
import { propuesta as t } from './textos-propuesta';

interface PropuestaDeSeccionProps {
  tenantId: string;
}

export function PropuestaDeSeccion({ tenantId }: PropuestaDeSeccionProps) {
  const queryClient = useQueryClient();
  const [resultado, setResultado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['propuesta-de-seccion', tenantId],
    queryFn: () => api.tenants.propuestaDeSeccion(tenantId),
    // Cada pedido es una llamada al modelo: no se repite en cada montaje.
    staleTime: 5 * 60 * 1000,
  });
  const propuesta = data?.propuesta ?? null;

  const contestar = useMutation({
    mutationFn: (acepta: boolean) => {
      if (!propuesta) throw new Error('sin propuesta');
      return api.tenants.contestarPropuestaDeSeccion(tenantId, {
        tema: propuesta.tema,
        ids: propuesta.ids,
        acepta,
      });
    },
    onSuccess: (_r, acepta) => {
      setError(null);
      setResultado(acepta && propuesta ? t.agrupadas(propuesta.titulo) : null);
      // El conocimiento cambió de forma: los temas del panel y la próxima
      // propuesta se vuelven a pedir.
      for (const clave of ['propuesta-de-seccion', 'conocimiento-temas', 'tenant-kb-existe']) {
        void queryClient.invalidateQueries({ queryKey: [clave, tenantId] });
      }
    },
    // 409: los hechos ya cambiaron (otra pestaña los movió). No es
    // transitorio: se vuelve a pedir la propuesta en vez de reintentar los
    // mismos ids (quinta revisión del 2026-09-24).
    onError: (e: Error) => {
      if (/^409/.test(e.message)) {
        // Como resultado y no como error: la propuesta se vuelve a pedir y
        // casi siempre desaparece, y un aviso adentro de la tarjeta se iba
        // con ella sin que nadie lo leyera.
        setResultado(t.yaCambiaron);
        void queryClient.invalidateQueries({ queryKey: ['propuesta-de-seccion', tenantId] });
        return;
      }
      setError(t.noSePudo);
    },
  });

  if (resultado) {
    return (
      <p className="panel-saber-aviso" data-testid="propuesta-de-seccion-lista">
        {resultado}
      </p>
    );
  }
  if (!propuesta) return null;

  return (
    <fieldset className="panel-saber-propuesta" data-testid="propuesta-de-seccion">
      <p>{t.pregunta(propuesta.ids.length, propuesta.titulo)}</p>
      {error && (
        <p className="alta-chat-error" role="alert">
          {error}
        </p>
      )}
      <div className="probar-correccion-acciones">
        <Button type="button" loading={contestar.isPending} onClick={() => contestar.mutate(true)}>
          {t.si}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={contestar.isPending}
          onClick={() => contestar.mutate(false)}
        >
          {t.no}
        </Button>
      </div>
    </fieldset>
  );
}
